import * as THREE from 'three'
import { Mob } from './Mob'
import Terrain from '../terrain'
import { InventoryItemType, ItemType } from '../items'
import { hasLineOfSight } from './utils'
import ProjectileManager from './projectiles'

const px = 0.0625

const createMat = (color: number, emissive = 0x000000, emissiveIntensity = 0) =>
  new THREE.MeshStandardMaterial({
    color,
    roughness: 0.9,
    metalness: 0,
    emissive,
    emissiveIntensity
  })

const makeBox = (
  width: number,
  height: number,
  depth: number,
  material: THREE.Material
) => new THREE.Mesh(
  new THREE.BoxGeometry(px * width, px * height, px * depth),
  material
)

const makeLimb = (
  width: number,
  height: number,
  depth: number,
  material: THREE.Material
) => {
  const limb = makeBox(width, height, depth, material)
  limb.geometry.translate(0, -px * height * 0.5, 0)
  return limb
}

const enableShadows = (mesh: THREE.Group) => {
  mesh.traverse(object => {
    if (object instanceof THREE.Mesh) {
      object.castShadow = true
      object.receiveShadow = true
    }
  })
}

export abstract class HostileMob extends Mob {
  attackTarget: THREE.Object3D | null = null
  onAttack: ((amount: number) => void) | null = null
  awarenessRange = 28
  attackRange = 1.55
  attackCooldown = 1.2
  attackTimer = 0
  attackDamage = 2
  environmentIsNight = true
  // line-of-sight aggro: re-checked on a throttle; short memory keeps the
  // chase alive when the player ducks behind cover (vanilla behavior)
  losTimer = 0
  hasLOS = false
  aggroMemory = 0
  // per-frame scratch vectors (no allocations in updateAI)
  toTargetScratch = new THREE.Vector3()
  eyeScratch = new THREE.Vector3()

  constructor(scene: THREE.Scene, terrain: Terrain, position: THREE.Vector3) {
    super(scene, terrain, position)
    this.isHostile = true
    this.countsAsArkAnimal = false
  }

  setCombatTarget(
    target: THREE.Object3D | null,
    onAttack: ((amount: number) => void) | null
  ) {
    this.attackTarget = target
    this.onAttack = onAttack
  }

  // returns true while this mob should actively hunt the target
  protected updateAggro(delta: number) {
    if (!this.attackTarget) return false

    this.losTimer -= delta
    if (this.losTimer <= 0) {
      this.losTimer = 0.4 + Math.random() * 0.2
      this.eyeScratch.copy(this.mesh.position)
      this.eyeScratch.y += 1.4
      this.hasLOS = hasLineOfSight(
        this.terrain,
        this.eyeScratch,
        this.attackTarget.position,
        this.awarenessRange + 6
      )
    }

    if (this.hasLOS) {
      this.aggroMemory = 5
    } else {
      this.aggroMemory = Math.max(0, this.aggroMemory - delta)
    }
    return this.aggroMemory > 0
  }

  updateAI(delta: number, canSwim = false) {
    this.attackTimer = Math.max(0, this.attackTimer - delta)

    if (!this.attackTarget || (!this.grounded && !canSwim)) {
      super.updateAI(delta, canSwim)
      return
    }

    const toTarget = this.toTargetScratch.subVectors(
      this.attackTarget.position,
      this.mesh.position
    )
    const verticalDistance = Math.abs(toTarget.y)
    toTarget.y = 0
    const distance = toTarget.length()

    if (distance > this.awarenessRange || !this.updateAggro(delta)) {
      super.updateAI(delta, canSwim)
      return
    }

    if (distance <= this.attackRange && verticalDistance < 2.4) {
      this.velocity.x = 0
      this.velocity.z = 0
      this.state = 'idle'

      if (this.attackTimer <= 0) {
        this.onAttack?.(this.attackDamage)
        this.attackTimer = this.attackCooldown
      }
      return
    }

    const direction = toTarget.normalize()
    this.velocity.x = direction.x * this.speed
    this.velocity.z = direction.z * this.speed
    this.state = 'walking'

    const targetRotation = Math.atan2(this.velocity.x, this.velocity.z)
    let diff = targetRotation - this.mesh.rotation.y
    while (diff < -Math.PI) diff += Math.PI * 2
    while (diff > Math.PI) diff -= Math.PI * 2
    this.mesh.rotation.y += diff * 7 * delta
  }
}

const skeletonBoneMat = createMat(0xd8d4c3)
const skeletonJointMat = createMat(0x9a978a)
const skeletonEyeMat = createMat(0x2a2720, 0xfff2a8, 0.25)
const bowWoodMat = createMat(0x6e4a1f)
const bowStringMat = createMat(0xe8e4d8)

const zombieSkinMat = createMat(0x5c8f46)
const zombieShirtMat = createMat(0x3f6f86)
const zombiePantsMat = createMat(0x3d315f)
const zombieEyeMat = createMat(0xf7f1d1, 0xd9ff8a, 0.25)

const creeperBodyMat = createMat(0x58a642)
const creeperPatchMat = createMat(0x347a32)
const creeperFaceMat = createMat(0x101010)

export class Zombie extends HostileMob {
  legL: THREE.Mesh
  legR: THREE.Mesh
  armL: THREE.Mesh
  armR: THREE.Mesh
  head: THREE.Mesh
  animationPhase = 0

  constructor(scene: THREE.Scene, terrain: Terrain, position: THREE.Vector3) {
    super(scene, terrain, position)
    this.speed = 1.25
    this.maxHealth = 18
    this.health = this.maxHealth
    this.attackDamage = 2
    this.attackCooldown = 1.5
    this.burnsInSunlight = true

    this.legL = makeLimb(4, 12, 4, zombiePantsMat)
    this.legR = makeLimb(4, 12, 4, zombiePantsMat)
    this.legL.position.set(px * 2, px * 12, 0)
    this.legR.position.set(-px * 2, px * 12, 0)
    this.mesh.add(this.legL, this.legR)

    const body = makeBox(8, 12, 4, zombieShirtMat)
    body.position.set(0, px * 18, 0)
    this.mesh.add(body)

    this.head = makeBox(8, 8, 8, zombieSkinMat)
    this.head.geometry.translate(0, px * 4, 0)
    this.head.position.set(0, px * 24, 0)
    this.mesh.add(this.head)

    const eyeL = makeBox(1, 1, 0.35, zombieEyeMat)
    const eyeR = makeBox(1, 1, 0.35, zombieEyeMat)
    eyeL.position.set(-px * 2, px * 4.6, px * 4.2)
    eyeR.position.set(px * 2, px * 4.6, px * 4.2)
    this.head.add(eyeL, eyeR)

    this.armL = makeLimb(3, 12, 3, zombieSkinMat)
    this.armR = makeLimb(3, 12, 3, zombieSkinMat)
    this.armL.position.set(px * 5.8, px * 23, px * 1.7)
    this.armR.position.set(-px * 5.8, px * 23, px * 1.7)
    this.mesh.add(this.armL, this.armR)

    enableShadows(this.mesh)
  }

  updateAnimation(delta: number): void {
    this.animationPhase += delta * (this.state === 'walking' ? 7 : 2.4)
    const attackLean = this.attackTimer > this.attackCooldown - 0.25 ? 0.35 : 0

    if (this.state === 'walking') {
      const swing = Math.sin(this.animationPhase) * 0.55
      this.legL.rotation.x = swing
      this.legR.rotation.x = -swing
      this.armL.rotation.x = -1.2 - swing * 0.22 - attackLean
      this.armR.rotation.x = -1.2 + swing * 0.22 - attackLean
      this.head.rotation.y = Math.sin(this.animationPhase * 0.5) * 0.08
    } else {
      this.legL.rotation.x = THREE.MathUtils.lerp(this.legL.rotation.x, 0, delta * 5)
      this.legR.rotation.x = THREE.MathUtils.lerp(this.legR.rotation.x, 0, delta * 5)
      this.armL.rotation.x = THREE.MathUtils.lerp(this.armL.rotation.x, -1.1 - attackLean, delta * 5)
      this.armR.rotation.x = THREE.MathUtils.lerp(this.armR.rotation.x, -1.1 - attackLean, delta * 5)
      this.head.rotation.y = Math.sin(this.animationPhase) * 0.05
    }
  }

  getDrops(): InventoryItemType[] {
    const drops: InventoryItemType[] = []
    const fleshCount = Math.floor(Math.random() * 3)
    for (let i = 0; i < fleshCount; i++) drops.push(ItemType.rottenFlesh)
    if (Math.random() < 0.2) drops.push(ItemType.bone)
    return drops
  }
}

export class Creeper extends HostileMob {
  legs: THREE.Mesh[] = []
  body: THREE.Mesh
  head: THREE.Mesh
  flashOverlay: THREE.Mesh
  flashMaterial: THREE.MeshBasicMaterial
  animationPhase = 0
  fuseTimer = 0
  fuseTime = 1.55
  fuseRange = 2.25
  cancelRange = 5.5
  exploding = false
  onExplode: ((position: THREE.Vector3) => void) | null = null

  constructor(scene: THREE.Scene, terrain: Terrain, position: THREE.Vector3) {
    super(scene, terrain, position)
    this.speed = 1.38
    this.maxHealth = 16
    this.health = this.maxHealth
    this.awarenessRange = 30
    this.attackDamage = 0
    this.attackRange = this.fuseRange

    for (const [x, z] of [
      [-2.2, 2.2],
      [2.2, 2.2],
      [-2.2, -2.2],
      [2.2, -2.2]
    ]) {
      const leg = makeLimb(3.5, 6, 3.5, creeperBodyMat)
      leg.position.set(px * x, px * 6, px * z)
      this.legs.push(leg)
      this.mesh.add(leg)
    }

    this.body = makeBox(8, 18, 4, creeperBodyMat)
    this.body.position.set(0, px * 15, 0)
    this.mesh.add(this.body)

    const patchA = makeBox(3, 6, 0.35, creeperPatchMat)
    const patchB = makeBox(2, 5, 0.35, creeperPatchMat)
    patchA.position.set(px * 2.5, px * 3, px * 2.2)
    patchB.position.set(-px * 2.2, -px * 3, px * 2.2)
    this.body.add(patchA, patchB)

    this.head = makeBox(8, 8, 8, creeperBodyMat)
    this.head.geometry.translate(0, px * 4, 0)
    this.head.position.set(0, px * 24, 0)
    this.mesh.add(this.head)

    const eyeL = makeBox(1.4, 1.4, 0.45, creeperFaceMat)
    const eyeR = makeBox(1.4, 1.4, 0.45, creeperFaceMat)
    const mouthTop = makeBox(1.7, 1.4, 0.45, creeperFaceMat)
    const mouthBottom = makeBox(3.2, 2.2, 0.45, creeperFaceMat)
    eyeL.position.set(-px * 2, px * 4.6, px * 4.25)
    eyeR.position.set(px * 2, px * 4.6, px * 4.25)
    mouthTop.position.set(0, px * 2.4, px * 4.25)
    mouthBottom.position.set(0, px * 0.4, px * 4.25)
    this.head.add(eyeL, eyeR, mouthTop, mouthBottom)

    this.flashMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0,
      depthWrite: false
    })
    this.flashOverlay = makeBox(9, 30, 9, this.flashMaterial)
    this.flashOverlay.position.set(0, px * 16, 0)
    this.flashOverlay.visible = false
    this.mesh.add(this.flashOverlay)

    enableShadows(this.mesh)
  }

  updateAI(delta: number, canSwim = false) {
    if (!this.attackTarget || (!this.grounded && !canSwim)) {
      this.coolFuse(delta)
      super.updateAI(delta, canSwim)
      return
    }

    const toTarget = this.toTargetScratch.subVectors(
      this.attackTarget.position,
      this.mesh.position
    )
    const verticalDistance = Math.abs(toTarget.y)
    toTarget.y = 0
    const distance = toTarget.length()

    if (distance > this.awarenessRange || !this.updateAggro(delta)) {
      this.coolFuse(delta)
      super.updateAI(delta, canSwim)
      return
    }

    const direction = toTarget.normalize()
    const targetRotation = Math.atan2(direction.x, direction.z)
    let diff = targetRotation - this.mesh.rotation.y
    while (diff < -Math.PI) diff += Math.PI * 2
    while (diff > Math.PI) diff -= Math.PI * 2
    this.mesh.rotation.y += diff * 8 * delta

    // a creeper only primes while it can actually see you
    if (
      this.hasLOS &&
      verticalDistance < 2.6 &&
      (distance <= this.fuseRange || (this.fuseTimer > 0 && distance <= this.cancelRange))
    ) {
      this.fuseTimer += delta
      this.velocity.x = distance <= this.fuseRange
        ? 0
        : direction.x * this.speed * 0.55
      this.velocity.z = distance <= this.fuseRange
        ? 0
        : direction.z * this.speed * 0.55
      this.state = distance <= this.fuseRange ? 'idle' : 'walking'

      if (this.fuseTimer >= this.fuseTime) {
        this.triggerExplosion()
      }
      return
    }

    this.coolFuse(delta)
    this.velocity.x = direction.x * this.speed
    this.velocity.z = direction.z * this.speed
    this.state = 'walking'
  }

  coolFuse(delta: number) {
    this.fuseTimer = Math.max(0, this.fuseTimer - delta * 1.35)
  }

  triggerExplosion() {
    if (this.exploding) return
    this.exploding = true
    this.dropsSpawned = true
    const position = this.mesh.position.clone()
    position.y += 0.7
    this.onExplode?.(position)
    this.despawn()
  }

  updateAnimation(delta: number): void {
    this.animationPhase += delta * (this.state === 'walking' ? 8 : 2.5)
    const swing = this.state === 'walking'
      ? Math.sin(this.animationPhase) * 0.35
      : 0
    this.legs.forEach((leg, index) => {
      leg.rotation.x = index % 2 === 0 ? swing : -swing
    })

    const fuseRatio = Math.min(1, this.fuseTimer / this.fuseTime)
    const pulse = fuseRatio > 0
      ? 1 + Math.sin(this.fuseTimer * 22) * 0.035 + fuseRatio * 0.08
      : 1
    this.mesh.scale.set(pulse, pulse, pulse)
    this.flashOverlay.visible = fuseRatio > 0 && Math.sin(this.fuseTimer * 24) > 0
    this.flashMaterial.opacity = 0.2 + fuseRatio * 0.45
    this.head.rotation.y = Math.sin(this.animationPhase * 0.5) * 0.06
  }

  getDrops(): InventoryItemType[] {
    const drops: InventoryItemType[] = []
    const gunpowderCount = Math.floor(Math.random() * 3)
    for (let i = 0; i < gunpowderCount; i++) drops.push(ItemType.gunpowder)
    return drops
  }
}

export class SkeletonGunner extends HostileMob {
  legL: THREE.Mesh
  legR: THREE.Mesh
  armL: THREE.Mesh
  armR: THREE.Mesh
  head: THREE.Mesh
  bow: THREE.Group
  animationPhase = 0
  bowRange = 20
  preferredRange = 8
  strafeDir: 1 | -1 = 1
  strafeTimer = 0
  projectiles: ProjectileManager | null = null
  arrowFromScratch = new THREE.Vector3()
  arrowToScratch = new THREE.Vector3()

  constructor(scene: THREE.Scene, terrain: Terrain, position: THREE.Vector3) {
    super(scene, terrain, position)
    this.speed = 1.15
    this.maxHealth = 16
    this.health = this.maxHealth
    this.awarenessRange = 34
    this.attackRange = this.bowRange
    this.attackDamage = 5
    this.attackCooldown = 2
    this.burnsInSunlight = true

    this.legL = makeLimb(2.2, 12, 2.2, skeletonBoneMat)
    this.legR = makeLimb(2.2, 12, 2.2, skeletonBoneMat)
    this.legL.position.set(px * 1.8, px * 12, 0)
    this.legR.position.set(-px * 1.8, px * 12, 0)
    this.mesh.add(this.legL, this.legR)

    const pelvis = makeBox(6, 2, 3, skeletonJointMat)
    pelvis.position.set(0, px * 13, 0)
    this.mesh.add(pelvis)

    const spine = makeBox(3, 10, 2.5, skeletonBoneMat)
    spine.position.set(0, px * 19, 0)
    this.mesh.add(spine)

    const ribs = makeBox(8, 5, 3, skeletonBoneMat)
    ribs.position.set(0, px * 20, px * 0.2)
    this.mesh.add(ribs)

    this.head = makeBox(7, 7, 7, skeletonBoneMat)
    this.head.geometry.translate(0, px * 3.5, 0)
    this.head.position.set(0, px * 24, 0)
    this.mesh.add(this.head)

    const eyeL = makeBox(1, 1, 0.35, skeletonEyeMat)
    const eyeR = makeBox(1, 1, 0.35, skeletonEyeMat)
    eyeL.position.set(-px * 1.7, px * 4, px * 3.7)
    eyeR.position.set(px * 1.7, px * 4, px * 3.7)
    this.head.add(eyeL, eyeR)

    this.armL = makeLimb(2, 12, 2, skeletonBoneMat)
    this.armR = makeLimb(2, 12, 2, skeletonBoneMat)
    this.armL.position.set(px * 5.2, px * 22.5, px * 1.8)
    this.armR.position.set(-px * 5.2, px * 22.5, px * 1.8)
    this.armL.rotation.x = -1.25
    this.armR.rotation.x = -1.25
    this.mesh.add(this.armL, this.armR)

    // bow held in the leading hand: grip + two slanted limbs + string
    this.bow = new THREE.Group()
    this.bow.position.set(px * 2.9, px * 18.2, px * 5)
    const grip = makeBox(1, 4, 1.2, bowWoodMat)
    const limbTop = makeBox(1, 4, 1, bowWoodMat)
    limbTop.position.set(0, px * 3.4, px * 1.1)
    limbTop.rotation.x = -0.55
    const limbBottom = makeBox(1, 4, 1, bowWoodMat)
    limbBottom.position.set(0, -px * 3.4, px * 1.1)
    limbBottom.rotation.x = 0.55
    const string = makeBox(0.35, 9.6, 0.35, bowStringMat)
    string.position.set(0, 0, px * 2.4)
    this.bow.add(grip, limbTop, limbBottom, string)
    this.mesh.add(this.bow)

    enableShadows(this.mesh)
  }

  updateAI(delta: number, canSwim = false) {
    this.attackTimer = Math.max(0, this.attackTimer - delta)

    if (!this.attackTarget || (!this.grounded && !canSwim)) {
      super.updateAI(delta, canSwim)
      return
    }

    const toTarget = this.toTargetScratch.subVectors(
      this.attackTarget.position,
      this.mesh.position
    )
    const verticalDistance = Math.abs(toTarget.y)
    toTarget.y = 0
    const distance = toTarget.length()

    if (distance > this.awarenessRange || !this.updateAggro(delta)) {
      super.updateAI(delta, canSwim)
      return
    }

    const direction = toTarget.normalize()
    const targetRotation = Math.atan2(direction.x, direction.z)
    let diff = targetRotation - this.mesh.rotation.y
    while (diff < -Math.PI) diff += Math.PI * 2
    while (diff > Math.PI) diff -= Math.PI * 2
    this.mesh.rotation.y += diff * 8 * delta

    this.strafeTimer -= delta
    if (this.strafeTimer <= 0) {
      this.strafeTimer = 1 + Math.random()
      if (Math.random() < 0.5) {
        this.strafeDir = this.strafeDir === 1 ? -1 : 1
      }
    }

    // no clear shot — push toward the player to regain sight
    if (!this.hasLOS) {
      this.velocity.x = direction.x * this.speed
      this.velocity.z = direction.z * this.speed
      this.state = 'walking'
      return
    }

    if (distance < this.preferredRange) {
      // backpedal away while keeping aim
      this.velocity.x = -direction.x * this.speed * 0.8
      this.velocity.z = -direction.z * this.speed * 0.8
      this.state = 'walking'
    } else if (distance <= this.bowRange) {
      // circle-strafe at shooting distance (vanilla kiting)
      this.velocity.x = -direction.z * this.strafeDir * this.speed * 0.6
      this.velocity.z = direction.x * this.strafeDir * this.speed * 0.6
      this.state = 'walking'
    } else {
      this.velocity.x = direction.x * this.speed
      this.velocity.z = direction.z * this.speed
      this.state = 'walking'
      return
    }

    if (
      distance <= this.bowRange &&
      verticalDistance < 10 &&
      this.attackTimer <= 0
    ) {
      this.shootArrow()
      this.attackTimer = this.attackCooldown
    }
  }

  shootArrow() {
    if (!this.projectiles || !this.attackTarget) return
    this.arrowFromScratch.copy(this.mesh.position)
    this.arrowFromScratch.y += 1.45
    // camera sits at eye height — aim for the chest
    this.arrowToScratch.copy(this.attackTarget.position)
    this.arrowToScratch.y -= 0.6
    this.projectiles.spawnArrow(
      this.arrowFromScratch,
      this.arrowToScratch,
      this.attackDamage
    )
  }

  updateAnimation(delta: number): void {
    this.animationPhase += delta * (this.state === 'walking' ? 7 : 2.4)
    const justFired = this.attackTimer > this.attackCooldown - 0.18
    // draw builds as the cooldown runs out
    const drawProgress = 1 - this.attackTimer / this.attackCooldown

    if (this.state === 'walking') {
      const swing = Math.sin(this.animationPhase) * 0.45
      this.legL.rotation.x = swing
      this.legR.rotation.x = -swing
      this.head.rotation.y = Math.sin(this.animationPhase * 0.5) * 0.06
    } else {
      this.legL.rotation.x = THREE.MathUtils.lerp(this.legL.rotation.x, 0, delta * 5)
      this.legR.rotation.x = THREE.MathUtils.lerp(this.legR.rotation.x, 0, delta * 5)
      this.head.rotation.y = Math.sin(this.animationPhase) * 0.04
    }

    // bow arm extended, draw arm pulls back with the draw
    this.armL.rotation.x = THREE.MathUtils.lerp(this.armL.rotation.x, -1.45, delta * 6)
    this.armR.rotation.x = THREE.MathUtils.lerp(
      this.armR.rotation.x,
      -1.2 - drawProgress * 0.3,
      delta * 6
    )
    this.bow.rotation.x = justFired
      ? 0.18
      : THREE.MathUtils.lerp(this.bow.rotation.x, -0.12 * drawProgress, delta * 8)
  }

  getDrops(): InventoryItemType[] {
    const drops: InventoryItemType[] = [ItemType.bone]
    if (Math.random() < 0.55) drops.push(ItemType.gunpowder)
    if (Math.random() < 0.25) drops.push(ItemType.bone)
    return drops
  }
}

const spiderBodyMat = createMat(0x1f1718)
const spiderLegMat = createMat(0x120d0e)
const spiderEyeMat = createMat(0xff2d2d, 0xff0000, 0.6)

export class Spider extends HostileMob {
  legs: THREE.Group[] = []
  head: THREE.Mesh
  animationPhase = 0
  // vanilla: spiders are neutral in daylight unless provoked
  provoked = false
  leapCooldown = 0

  constructor(scene: THREE.Scene, terrain: Terrain, position: THREE.Vector3) {
    super(scene, terrain, position)
    this.speed = 1.6
    this.maxHealth = 14
    this.health = this.maxHealth
    this.attackDamage = 1
    this.attackCooldown = 1.25
    this.attackRange = 1.85

    const abdomen = makeBox(12, 7, 10, spiderBodyMat)
    abdomen.position.set(0, px * 5, -px * 2.5)
    this.mesh.add(abdomen)

    this.head = makeBox(8, 6, 6, spiderBodyMat)
    this.head.position.set(0, px * 5.3, px * 6)
    this.mesh.add(this.head)

    for (let i = 0; i < 4; i++) {
      const z = px * (4 - i * 2.7)
      const angle = (i - 1.5) * 0.28
      this.addLeg(1, z, angle)
      this.addLeg(-1, z, -angle)
    }

    const eyeL = makeBox(1.4, 1.1, 0.5, spiderEyeMat)
    const eyeR = makeBox(1.4, 1.1, 0.5, spiderEyeMat)
    eyeL.position.set(-px * 1.8, px * 1, px * 3.25)
    eyeR.position.set(px * 1.8, px * 1, px * 3.25)
    this.head.add(eyeL, eyeR)

    this.mesh.scale.setScalar(1.1)
    enableShadows(this.mesh)
  }

  addLeg(side: 1 | -1, z: number, yaw: number) {
    const leg = new THREE.Group()
    leg.position.set(px * side * 4.5, px * 4.8, z)
    leg.rotation.y = yaw
    leg.rotation.z = side * 0.28

    const segment = makeBox(9, 1, 1, spiderLegMat)
    segment.geometry.translate(px * side * 4.5, 0, 0)
    leg.add(segment)
    this.legs.push(leg)
    this.mesh.add(leg)
  }

  takeDamage(amount: number, knockbackDir?: THREE.Vector3) {
    super.takeDamage(amount, knockbackDir)
    this.provoked = true
  }

  // spiders climb walls instead of jumping at them
  handleBlocked() {
    this.velocity.y = 3.2
    this.grounded = false
  }

  updateAI(delta: number, canSwim = false) {
    this.leapCooldown = Math.max(0, this.leapCooldown - delta)

    // calm down once the player is long gone
    if (this.provoked && this.attackTarget) {
      const dx = this.attackTarget.position.x - this.mesh.position.x
      const dz = this.attackTarget.position.z - this.mesh.position.z
      if (dx * dx + dz * dz > (this.awarenessRange + 12) ** 2) {
        this.provoked = false
      }
    }

    // daylight + unprovoked = harmless wanderer
    if (!this.environmentIsNight && !this.provoked) {
      this.attackTimer = Math.max(0, this.attackTimer - delta)
      this.passiveBehavior(delta, canSwim)
      return
    }

    // pounce when at mid-range with a clear line
    if (
      this.attackTarget &&
      this.grounded &&
      this.leapCooldown <= 0 &&
      this.hasLOS
    ) {
      const toTarget = this.toTargetScratch.subVectors(
        this.attackTarget.position,
        this.mesh.position
      )
      toTarget.y = 0
      const distance = toTarget.length()
      if (distance > 3 && distance < 6) {
        const direction = toTarget.normalize()
        this.velocity.x = direction.x * 6.5
        this.velocity.z = direction.z * 6.5
        this.velocity.y = 6
        this.grounded = false
        this.leapCooldown = 2.5
        this.state = 'walking'
        this.attackTimer = Math.max(0, this.attackTimer - delta)
        return
      }
    }

    super.updateAI(delta, canSwim)
  }

  updateAnimation(delta: number): void {
    this.animationPhase += delta * (this.state === 'walking' ? 10 : 2.2)

    this.legs.forEach((leg, index) => {
      const side = leg.position.x >= 0 ? 1 : -1
      const phase = this.animationPhase + index * 0.8
      const walkSwing = this.state === 'walking' ? Math.sin(phase) * 0.24 : 0
      leg.rotation.z = side * (0.28 + walkSwing)
    })

    this.head.rotation.x = this.attackTimer > this.attackCooldown - 0.18
      ? -0.18
      : Math.sin(this.animationPhase * 0.7) * 0.04
  }

  getDrops(): InventoryItemType[] {
    const drops: InventoryItemType[] = []
    const stringCount = Math.floor(Math.random() * 3)
    for (let i = 0; i < stringCount; i++) drops.push(ItemType.string)
    if (Math.random() < 0.33) drops.push(ItemType.spiderEye)
    return drops
  }
}
